import React from 'react';
import { BookOpen, Users, Target, Zap, Shield, Globe } from 'lucide-react';

export function Home() {
  const features = [
    {
      icon: BookOpen,
      title: 'Generación de Documentos',
      description: 'Crea proyectos, unidades y sesiones educativas con IA'
    },
    {
      icon: Target,
      title: 'Evaluación Inteligente',
      description: 'Diseña instrumentos de evaluación personalizados'
    },
    {
      icon: Users,
      title: 'Gestión Educativa',
      description: 'Administra documentos de gestión institucional'
    },
    {
      icon: Zap,
      title: 'IA Avanzada',
      description: 'Potenciado por OpenAI para resultados de calidad'
    },
    {
      icon: Shield,
      title: 'Seguro y Confiable',
      description: 'Tus datos están protegidos con las mejores prácticas'
    },
    {
      icon: Globe,
      title: 'Acceso Universal',
      description: 'Disponible desde cualquier dispositivo con internet'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-indigo-100 rounded-full">
            <BookOpen className="w-12 h-12 text-indigo-600" />
          </div>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
          Bienvenido a <span className="text-indigo-600">PROFE JHONY</span>
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          La plataforma educativa inteligente que revoluciona la forma de crear y gestionar 
          contenido educativo con el poder de la inteligencia artificial.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/projects"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-200"
          >
            Comenzar Ahora
          </a>
          <a
            href="/management"
            className="inline-flex items-center px-6 py-3 border border-indigo-600 text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 transition-colors duration-200"
          >
            Ver Documentos
          </a>
        </div>
      </div>

      {/* Features Grid */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          ¿Qué puedes hacer con PROFE JHONY?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <feature.icon className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 ml-3">
                  {feature.title}
                </h3>
              </div>
              <p className="text-gray-600">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-8 mb-16">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Beneficios de usar nuestra plataforma
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Optimiza tu tiempo y mejora la calidad de tus documentos educativos
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
              <div>
                <h4 className="font-semibold text-gray-900">Ahorro de Tiempo</h4>
                <p className="text-gray-600">Genera documentos en minutos, no en horas</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
              <div>
                <h4 className="font-semibold text-gray-900">Calidad Profesional</h4>
                <p className="text-gray-600">Contenido estructurado y pedagógicamente sólido</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
              <div>
                <h4 className="font-semibold text-gray-900">Personalización</h4>
                <p className="text-gray-600">Adapta el contenido a tus necesidades específicas</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
              <div>
                <h4 className="font-semibold text-gray-900">Integración Curricular</h4>
                <p className="text-gray-600">Alineado con el Diseño Curricular Nacional</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
              <div>
                <h4 className="font-semibold text-gray-900">Fácil de Usar</h4>
                <p className="text-gray-600">Interfaz intuitiva y amigable para todos</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
              <div>
                <h4 className="font-semibold text-gray-900">Soporte Continuo</h4>
                <p className="text-gray-600">Actualizaciones y mejoras constantes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          ¿Listo para comenzar?
        </h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          Sube tu Diseño Curricular Nacional (DCN) y comienza a generar contenido educativo 
          de alta calidad con la ayuda de la inteligencia artificial.
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto">
          <p className="text-yellow-800 text-sm">
            <strong>Consejo:</strong> Para obtener mejores resultados, asegúrate de cargar tu DCN 
            antes de generar documentos. Esto permitirá que la IA cree contenido más alineado 
            con tus objetivos curriculares.
          </p>
        </div>
      </div>
    </div>
  );
}